import { useState } from 'react';
import { Container, Title, Button, Group, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconPlus } from '@tabler/icons-react';
import { useOrganization } from '@/hooks/useOrganization';
import {
  usePeople,
  usePerson,
  useCreatePerson,
  useUpdatePerson,
  useDeletePerson,
} from '@/hooks/api/usePeople';
import { PeopleTable } from './components/PeopleTable';
import { PersonForm } from './components/PersonForm';
import { PersonDetailsModal } from './components/PersonDetailsModal';
import type { Person } from '@/types/models';
import type { PersonFormValues } from './schemas/personSchema';

export function PeoplePage() {
  const { organizationId } = useOrganization();
  const { data: people = [], isLoading } = usePeople(organizationId);
  const createPerson = useCreatePerson();
  const updatePerson = useUpdatePerson();
  const deletePerson = useDeletePerson();

  const [formOpened, setFormOpened] = useState(false);
  const [detailsOpened, setDetailsOpened] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Fetch detailed person data when viewing details
  const { data: personDetails } = usePerson(selectedPersonId || undefined);

  const handleCreate = () => {
    setSelectedPerson(null);
    setIsEditing(false);
    setFormOpened(true);
  };

  const handleEdit = (person: Person) => {
    setSelectedPerson(person);
    setIsEditing(true);
    setFormOpened(true);
  };

  const handleView = (person: Person) => {
    setSelectedPersonId(person.id);
    setDetailsOpened(true);
  };

  const handleDelete = (person: Person) => {
    modals.openConfirmModal({
      title: 'Delete Person',
      children: (
        <Text size="sm">
          Are you sure you want to delete <strong>{person.full_name}</strong>? This action cannot
          be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deletePerson.mutate(person.id);
      },
    });
  };

  const handleSubmit = async (values: PersonFormValues, keepOpen = false) => {
    if (!organizationId) return;

    if (isEditing && selectedPerson) {
      await updatePerson.mutateAsync({
        id: selectedPerson.id,
        data: values,
      });
    } else {
      await createPerson.mutateAsync({
        ...values,
        organization_id: organizationId,
      });
    }

    if (!keepOpen) {
      setFormOpened(false);
      setSelectedPerson(null);
    }
  };

  return (
    <Container fluid>
      <Stack gap="lg">
        <Group justify="apart">
          <div>
            <Title order={1}>People</Title>
            <Text c="dimmed" size="sm">
              Manage people in your organization
            </Text>
          </div>
          <Button leftSection={<IconPlus size={16} />} onClick={handleCreate}>
            Add Person
          </Button>
        </Group>

        <PeopleTable
          people={people}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isLoading={isLoading}
        />

        <PersonForm
          opened={formOpened}
          onClose={() => {
            setFormOpened(false);
            setSelectedPerson(null);
          }}
          onSubmit={handleSubmit}
          person={isEditing ? selectedPerson : null}
          isLoading={createPerson.isPending || updatePerson.isPending}
        />

        <PersonDetailsModal
          opened={detailsOpened}
          onClose={() => {
            setDetailsOpened(false);
            setSelectedPersonId(null);
          }}
          person={personDetails}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </Stack>
    </Container>
  );
}
